import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
    getActiveServerSlug,
    getOtherServerSlug,
    serverDisplayName,
    serverGuildsPath,
    serverRankingsPath,
    serverReportsPath,
    swapServerInPath,
} from "../utils/siteContext";

function brandImage(serverSlug: string, active: boolean) {
    const prefix = serverSlug === "fangbreaker" ? "fang" : "frost";
    return `/${prefix}-${active ? "active" : "inactive"}.svg`;
}

export function AppNavbar() {
    const auth = useAuth();
    const location = useLocation();
    const activeServerSlug = getActiveServerSlug(location.pathname);
    const inactiveServerSlug = getOtherServerSlug(activeServerSlug);
    const inactivePath = swapServerInPath(
        `${location.pathname}${location.search}${location.hash}`,
        inactiveServerSlug,
    );
    const activeRankingsPath = serverRankingsPath(activeServerSlug);
    const activeHomePath = "/";

    return (
        <header className="site-header">
            <a className="visually-hidden-focusable skip-link" href="#main-content">
                Skip to main content
            </a>

            <div className="site-branding">
                <div className="content-wrapper">
                    <div className="site-branding__inner">
                        <NavLink
                            className="site-brand-link is-active"
                            to={activeHomePath}
                            aria-label={`${serverDisplayName(activeServerSlug)} rankings home`}
                        >
                            <img
                                src={brandImage(activeServerSlug, true)}
                                alt={`${serverDisplayName(activeServerSlug)}.zone`}
                            />
                        </NavLink>
                        <a
                            className="site-brand-link"
                            href={inactivePath}
                            aria-label={`Switch to ${serverDisplayName(inactiveServerSlug)} view`}
                        >
                            <img
                                src={brandImage(inactiveServerSlug, false)}
                                alt={`${serverDisplayName(inactiveServerSlug)}.zone`}
                            />
                        </a>
                    </div>
                </div>
            </div>

            <nav
                className="navbar navbar-expand-lg site-navbar"
                aria-label="Main navigation"
            >
                <div className="content-wrapper">
                    <button
                        className="navbar-toggler"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#mainNav"
                        aria-controls="mainNav"
                        aria-expanded="false"
                        aria-label="Toggle navigation"
                    >
                        <span className="navbar-toggler-icon" />
                    </button>
                    <div className="collapse navbar-collapse" id="mainNav">
                        <ul className="navbar-nav me-auto mb-2 mb-lg-0">
                            <li className="nav-item">
                                <NavLink className="nav-link" to="/">
                                    Home
                                </NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink className="nav-link" to="/resource-hunter">
                                    Resource Hunter
                                </NavLink>
                            </li>

                            <li className="nav-item">
                                <NavLink
                                    className="nav-link"
                                    to={serverGuildsPath(activeServerSlug)}
                                >
                                    Guilds
                                </NavLink>
                            </li>

                            <li className="nav-item dropdown">
                                <button
                                    className="nav-link dropdown-toggle btn btn-link"
                                    type="button"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                >
                                    Parsing
                                </button>
                                <ul className="dropdown-menu dropdown-menu-dark">
                                    <li>
                                        <NavLink className="dropdown-item" to={activeRankingsPath}>
                                            Rankings
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink className="dropdown-item" to={serverReportsPath(activeServerSlug)}>
                                            Reports
                                        </NavLink>
                                    </li>
                                    <li>
                                        <NavLink className="dropdown-item" to="/client">Client</NavLink>
                                    </li>
                                </ul>
                            </li>

                            <li className="nav-item dropdown">
                                <button
                                    className="nav-link dropdown-toggle btn btn-link"
                                    type="button"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                >
                                    Community Resources
                                </button>
                                <ul className="dropdown-menu dropdown-menu-dark">
                                    <li>
                                        <NavLink className="dropdown-item" to="/vaniki-bros">
                                            Vaniki Bros Acheivement Co
                                        </NavLink>
                                    </li>
                                </ul>
                            </li>


                            <li className="nav-item">
                                <NavLink className="nav-link" to="/support">Support</NavLink>
                            </li>

                            {auth.isAdmin ? (
                                <li className="nav-item dropdown">
                                    <button
                                        className="nav-link dropdown-toggle btn btn-link"
                                        type="button"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                    >
                                        Admin
                                    </button>
                                    <ul className="dropdown-menu dropdown-menu-dark">
                                        <li>
                                            <NavLink className="dropdown-item" to="/admin/servers">
                                                Servers
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink
                                                className="dropdown-item"
                                                to="/admin/raid-content"
                                            >
                                                Raid Content
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink className="dropdown-item" to="/admin/guilds">
                                                Guilds
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink className="dropdown-item" to="/admin/bonuses">
                                                Bonus Moderation
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink className="dropdown-item" to="/admin/users">
                                                Users
                                            </NavLink>
                                        </li>
                                        <li>
                                            <NavLink className="dropdown-item" to="/admin/bonus-zones">
                                                Bonus Zones
                                            </NavLink>
                                        </li>
                                    </ul>
                                </li>
                            ) : null}
                        </ul>

                        <div className="d-flex align-items-center gap-2">
                            {auth.isLoading ? (
                                <span className="text-body-secondary small">
                                    Checking session...
                                </span>
                            ) : auth.isAuthenticated && auth.user ? (
                                <div className="dropdown">
                                    <button
                                        className="btn btn-outline-light btn-sm dropdown-toggle"
                                        type="button"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                    >
                                        <i className="bi bi-person-circle me-1" />{" "}
                                        {auth.user.displayName || auth.user.username}
                                    </button>
                                    <ul className="dropdown-menu dropdown-menu-dark dropdown-menu-end">
                                        <li>
                                            <h6 className="dropdown-header">{auth.user.role}</h6>
                                        </li>
                                        <li>
                                            <NavLink className="dropdown-item" to="/account">
                                                Account
                                            </NavLink>
                                        </li>
                                        <li>
                                            <hr className="dropdown-divider" />
                                        </li>
                                        <li>
                                            <button
                                                className="dropdown-item"
                                                type="button"
                                                onClick={() => void auth.logout()}
                                            >
                                                Logout
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            ) : (
                                <button
                                    className="btn btn-primary btn-sm"
                                    type="button"
                                    onClick={auth.login}
                                >
                                    <i className="bi bi-discord me-1" /> Login
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </nav>
        </header>
    );
}
